import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
} from "lucide-react";
import {
  ARTIFACT_LABELS,
  ARTIFACT_ORDER,
  type ArtifactType,
  type JobArtifact,
  type JobRun,
  type JobWithArtifacts,
} from "@shared/types";
import { api, ApiError } from "../api/client.ts";
import { ArtifactStatusBadge, JobStatusBadge } from "../components/StatusBadge.tsx";
import { formatDateTime, formatMoney, toDatetimeLocal } from "../lib/format.ts";

type LoadState =
  | { phase: "loading" }
  | { phase: "notfound" }
  | { phase: "error"; message: string }
  | { phase: "ready"; job: JobWithArtifacts };

// datetime-local gives us "YYYY-MM-DDTHH:mm" in local time; turn it back into an
// ISO timestamp the API expects, or null when the field is cleared.
function fromDatetimeLocal(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

const RUN_STATUS_STYLES: Record<JobRun["status"], string> = {
  ok: "bg-emerald-100 text-emerald-800 ring-emerald-200",
  error: "bg-rose-100 text-rose-800 ring-rose-200",
};

function stepLabel(step: JobRun["step"]): string {
  return step === "orchestrate" ? "Orchestrate" : ARTIFACT_LABELS[step];
}

export function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>({ phase: "loading" });

  const load = useCallback(async () => {
    if (!id) {
      setState({ phase: "notfound" });
      return;
    }
    setState({ phase: "loading" });
    try {
      const job = await api.getJob(id);
      setState({ phase: "ready", job });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setState({ phase: "notfound" });
      } else {
        setState({ phase: "error", message: e instanceof Error ? e.message : String(e) });
      }
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.phase === "loading") {
    return <div className="py-16 text-center text-slate-400">Loading…</div>;
  }

  if (state.phase === "notfound") {
    return (
      <div className="card mx-auto max-w-md p-8 text-center">
        <p className="font-semibold text-slate-900">Job not found</p>
        <p className="mt-2 text-sm text-slate-600">
          This job may have been removed, or the link is incorrect.
        </p>
        <Link to="/jobs" className="btn-secondary mt-4">
          <ArrowLeft size={16} />
          Back to jobs
        </Link>
      </div>
    );
  }

  if (state.phase === "error") {
    return (
      <div className="card mx-auto max-w-md p-8 text-center">
        <p className="font-semibold text-rose-700">Couldn’t load this job</p>
        <p className="mt-2 text-sm text-slate-600">{state.message}</p>
        <button onClick={() => void load()} className="btn-secondary mt-4">
          <RefreshCw size={16} />
          Try again
        </button>
      </div>
    );
  }

  return (
    <JobDetailReady job={state.job} onJob={(job) => setState({ phase: "ready", job })} />
  );
}

function JobDetailReady({
  job,
  onJob,
}: {
  job: JobWithArtifacts;
  onJob: (job: JobWithArtifacts) => void;
}) {
  const [rerunning, setRerunning] = useState(false);
  // Which per-step request is in flight, keyed by artifact type.
  const [pendingStep, setPendingStep] = useState<ArtifactType | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const artifactByType = new Map(job.artifacts.map((a) => [a.type, a] as const));

  async function runAll() {
    setActionError(null);
    setRerunning(true);
    try {
      onJob(await api.runJob(job.id));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setRerunning(false);
    }
  }

  async function retryStep(type: ArtifactType) {
    setActionError(null);
    setPendingStep(type);
    try {
      onJob(await api.retryStep(job.id, type));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setPendingStep(null);
    }
  }

  const busy = rerunning || pendingStep !== null;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/jobs" className="btn-ghost -ml-2 px-2 text-sm">
          <ArrowLeft size={16} />
          Jobs
        </Link>
      </div>

      <Header job={job} onRunAll={runAll} rerunning={rerunning} disabled={busy} />

      {actionError && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {actionError}
        </div>
      )}

      <section className="card overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Artifacts</h2>
        </div>
        <ul className="divide-y divide-slate-100">
          {ARTIFACT_ORDER.map((type) => (
            <ArtifactRow
              key={type}
              type={type}
              artifact={artifactByType.get(type)}
              onAction={() => retryStep(type)}
              pending={pendingStep === type}
              disabled={busy}
            />
          ))}
        </ul>
      </section>

      <EditSchedule job={job} onSaved={onJob} />

      <ActivityRuns jobId={job.id} />
    </div>
  );
}

function Header({
  job,
  onRunAll,
  rerunning,
  disabled,
}: {
  job: JobWithArtifacts;
  onRunAll: () => void;
  rerunning: boolean;
  disabled: boolean;
}) {
  return (
    <div className="card p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-bold text-slate-900">{job.customer_name}</h1>
            <JobStatusBadge status={job.status} />
          </div>
          <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin size={16} className="shrink-0 text-slate-400" />
              <span className="truncate">{job.address || "—"}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Phone size={16} className="shrink-0 text-slate-400" />
              <span className="truncate">{job.phone || "—"}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Mail size={16} className="shrink-0 text-slate-400" />
              <span className="truncate">{job.email || "—"}</span>
            </div>
          </dl>
          <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-3">
            <Detail label="Removal" value={formatDateTime(job.removal_at)} />
            <Detail label="Reinstall" value={formatDateTime(job.reinstall_at)} />
            <Detail label="Invoice amount" value={formatMoney(job.invoice_amount)} />
          </dl>
        </div>
        <button onClick={onRunAll} disabled={disabled} className="btn-primary shrink-0">
          <RefreshCw size={16} className={rerunning ? "animate-spin" : undefined} />
          {rerunning ? "Running…" : "Re-run all"}
        </button>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

function ArtifactRow({
  type,
  artifact,
  onAction,
  pending,
  disabled,
}: {
  type: ArtifactType;
  artifact: JobArtifact | undefined;
  onAction: () => void;
  pending: boolean;
  disabled: boolean;
}) {
  const status = artifact?.status ?? "pending";
  const showError = (status === "failed" || status === "draft") && artifact?.error;

  // Action label depends on the artifact's current lifecycle state.
  const actionLabel =
    status === "failed"
      ? "Retry"
      : status === "draft"
        ? "Complete draft"
        : status === "pending"
          ? "Run"
          : null; // `done` needs no action

  return (
    <li className="flex flex-wrap items-center gap-3 px-5 py-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-900">{ARTIFACT_LABELS[type]}</span>
          <ArtifactStatusBadge status={status} />
        </div>
        {showError && <p className="mt-1 text-xs text-rose-600">{artifact?.error}</p>}
      </div>
      <div className="flex items-center gap-2">
        {artifact?.url && (
          <a
            href={artifact.url}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost px-2 py-1 text-sm text-brand-700 hover:text-brand-800"
          >
            Open
            <ExternalLink size={14} />
          </a>
        )}
        {actionLabel && (
          <button
            onClick={onAction}
            disabled={disabled}
            className="btn-secondary px-3 py-1.5 text-sm"
          >
            {pending ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                Working…
              </>
            ) : (
              actionLabel
            )}
          </button>
        )}
      </div>
    </li>
  );
}

function EditSchedule({
  job,
  onSaved,
}: {
  job: JobWithArtifacts;
  onSaved: (job: JobWithArtifacts) => void;
}) {
  const [open, setOpen] = useState(false);
  const [removalAt, setRemovalAt] = useState(toDatetimeLocal(job.removal_at));
  const [reinstallAt, setReinstallAt] = useState(toDatetimeLocal(job.reinstall_at));
  const [invoiceAmount, setInvoiceAmount] = useState(
    job.invoice_amount === null || job.invoice_amount === undefined
      ? ""
      : String(job.invoice_amount),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form to the latest job whenever it changes (e.g. after a save/refresh).
  useEffect(() => {
    setRemovalAt(toDatetimeLocal(job.removal_at));
    setReinstallAt(toDatetimeLocal(job.reinstall_at));
    setInvoiceAmount(
      job.invoice_amount === null || job.invoice_amount === undefined
        ? ""
        : String(job.invoice_amount),
    );
  }, [job.removal_at, job.reinstall_at, job.invoice_amount]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const parsedAmount = invoiceAmount.trim() === "" ? null : Number(invoiceAmount);
    if (parsedAmount !== null && Number.isNaN(parsedAmount)) {
      setError("Invoice amount must be a number.");
      return;
    }
    const removalIso = fromDatetimeLocal(removalAt);
    if (!removalIso) {
      setError("Removal date/time is required.");
      return;
    }
    setSaving(true);
    try {
      const updated = await api.patchJob(job.id, {
        removal_at: removalIso,
        reinstall_at: fromDatetimeLocal(reinstallAt),
        invoice_amount: parsedAmount,
      });
      onSaved(updated);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-3 text-left"
        aria-expanded={open}
      >
        <h2 className="text-sm font-semibold text-slate-900">Edit schedule</h2>
        {open ? (
          <ChevronDown size={16} className="text-slate-400" />
        ) : (
          <ChevronRight size={16} className="text-slate-400" />
        )}
      </button>
      {open && (
        <form onSubmit={save} className="space-y-4 border-t border-slate-200 px-5 py-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label" htmlFor="removal_at">
                Removal
              </label>
              <input
                id="removal_at"
                type="datetime-local"
                className="input"
                value={removalAt}
                onChange={(e) => setRemovalAt(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="reinstall_at">
                Reinstall
              </label>
              <input
                id="reinstall_at"
                type="datetime-local"
                className="input"
                value={reinstallAt}
                onChange={(e) => setReinstallAt(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="invoice_amount">
                Invoice amount
              </label>
              <input
                id="invoice_amount"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                className="input"
                value={invoiceAmount}
                onChange={(e) => setInvoiceAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex items-center gap-2">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => setOpen(false)}
              className="btn-ghost"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function ActivityRuns({ jobId }: { jobId: string }) {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState<JobRun[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadRuns() {
    setLoading(true);
    setError(null);
    try {
      setRuns(await api.getRuns(jobId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && runs === null && !loading) void loadRuns();
  }

  return (
    <section className="card overflow-hidden">
      <button
        onClick={toggle}
        className="flex w-full items-center justify-between px-5 py-3 text-left"
        aria-expanded={open}
      >
        <h2 className="text-sm font-semibold text-slate-900">Activity / runs</h2>
        {open ? (
          <ChevronDown size={16} className="text-slate-400" />
        ) : (
          <ChevronRight size={16} className="text-slate-400" />
        )}
      </button>
      {open && (
        <div className="border-t border-slate-200">
          {loading && <p className="px-5 py-4 text-sm text-slate-400">Loading runs…</p>}
          {error && (
            <div className="flex items-center justify-between px-5 py-4 text-sm text-rose-600">
              <span>{error}</span>
              <button onClick={() => void loadRuns()} className="btn-ghost px-2 py-1 text-sm">
                <RefreshCw size={14} />
                Retry
              </button>
            </div>
          )}
          {!loading && !error && runs && runs.length === 0 && (
            <p className="px-5 py-4 text-sm text-slate-400">No runs recorded yet.</p>
          )}
          {!loading && !error && runs && runs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-5 py-2 font-medium">Step</th>
                    <th className="px-5 py-2 font-medium">Attempt</th>
                    <th className="px-5 py-2 font-medium">Status</th>
                    <th className="px-5 py-2 font-medium">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {runs.map((run) => (
                    <tr key={run.id}>
                      <td className="px-5 py-2 text-slate-900">{stepLabel(run.step)}</td>
                      <td className="px-5 py-2 text-slate-600">{run.attempt}</td>
                      <td className="px-5 py-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${RUN_STATUS_STYLES[run.status]}`}
                        >
                          {run.status === "ok" ? "OK" : "Error"}
                        </span>
                      </td>
                      <td className="px-5 py-2 text-slate-600">{formatDateTime(run.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
